import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import moment from 'moment';
import IPaymentTransaction from '../../models/IPaymentTransaction';
import { formatMoney } from '../../pipes/PriceFormatter';

const SUCCESS_STATUSES = ['authorize', 'authorized', 'capture', 'captured', 'paid', 'success', 'successful', 'succeeded', 'completed', 'settled'];

function DetailRow({ label, value, bold }: { label: string; value: React.ReactNode; bold?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
      <Typography variant="body2" color="text.secondary" fontWeight={bold ? 700 : 400}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={bold ? 700 : 500}
        sx={{ textAlign: 'right', wordBreak: 'break-all' }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function PaymentDetails({ transactions }: { transactions: IPaymentTransaction[] }) {
  const theme = useTheme();
  if (!transactions.length) {
    return null;
  }
  const accent = theme.palette.layoutColor?.main || theme.palette.success.main;
  const accentText = theme.palette.layoutColor?.contrastText || theme.palette.success.contrastText;

  return (
    <Stack spacing={2} sx={{ my: 3, width: '100%', maxWidth: 480, mx: 'auto' }}>
      {transactions.map((transaction, index) => {
        const status = transaction.cCStatus || '';
        const isPaid = SUCCESS_STATUSES.includes(status.toLowerCase());
        const transactionId = transaction.paymentProcessorTransactionId || transaction.cCTransactionId;
        const card = [transaction.cardIssuer, transaction.maskedCardNumber].filter(Boolean).join(' ');
        const tip = transaction.totalTip || 0;

        return (
          <Paper
            key={transactionId || index}
            variant="outlined"
            sx={{ borderRadius: 3, overflow: 'hidden', borderColor: alpha(accent, 0.35) }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ px: 2.5, py: 2, bgcolor: alpha(accent, 0.08), borderBottom: `3px solid ${accent}` }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: accent,
                  color: accentText,
                  flexShrink: 0,
                }}
              >
                <CheckCircleRoundedIcon />
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="h6" lineHeight={1.2}>Payment Details</Typography>
                {transaction.dateApplied && (
                  <Typography variant="caption" color="text.secondary">
                    {moment(transaction.dateApplied).format('MMM D, YYYY h:mm A')}
                  </Typography>
                )}
              </Box>
              {status && (
                <Chip
                  label={status}
                  size="small"
                  color={isPaid ? 'success' : 'warning'}
                  sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                />
              )}
            </Stack>

            <Stack spacing={1.25} sx={{ px: 2.5, py: 2 }}>
              {transactionId && <DetailRow label="Transaction ID" value={transactionId} />}
              {card && (
                <DetailRow
                  label="Card"
                  value={
                    <Stack component="span" direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
                      <CreditCardRoundedIcon fontSize="small" sx={{ color: accent }} />
                      <span>{card}</span>
                    </Stack>
                  }
                />
              )}
              {tip > 0 && <Divider flexItem />}
              {tip > 0 && <DetailRow label="Tip" value={`$${formatMoney(tip)}`} />}
              <Divider flexItem />
              <DetailRow label="Total Paid" value={`$${formatMoney(transaction.totalPaid || 0)}`} bold />
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
